import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '../../Breadcrumb/Breadcrumb';
import UserProfileCard from '../../UserProfile/UserProfileCard/UserProfileCard';

export default function UserProfilePage(props) {
	// console.log('UserProfilePage - props', props);
	const { id } = useParams();
	const [
		loading,
		setLoading
	] = useState(true);
	const [
		error,
		setError
	] = useState(null);
	const [
		userData,
		setUserData
	] = useState({
		cdxIdData: {},
		summaryData: {},
		personalData: {},
		loginData: {},
		customData: {},
		userLearningPlans: [],
		userAssessments: []
	});

	const userCdxIdDataUrl = useMemo(
		() => `${props.iam_to_cdx_user_mapping_endpoint}?user_id=${id}`,
		[
			id
		]
	);
	const userSummaryDataUrl = useMemo(
		() => `${props.iam_get_user_summary_endpoint}?user_id=${id}`,
		[
			id
		]
	);
	const userPersonalDataUrl = useMemo(
		() => `${props.iam_get_user_personal_endpoint}?user_id=${id}`,
		[
			id
		]
	);
	const userLoginDataUrl = useMemo(
		() => `${props.iam_get_user_login_endpoint}?user_id=${id}`,
		[
			id
		]
	);
	const userCustomDataUrl = useMemo(
		() => `${props.iam_get_user_custom_endpoint}?user_id=${id}`,
		[
			id
		]
	);

	useEffect(
		() => {
			const fetchData = async () => {
				try {
					const cdxIdResponse = await fetch(userCdxIdDataUrl);
					const cdxIdData = await cdxIdResponse.json();
					const cdxId = cdxIdData.data['ffg.app.user.id'];

					const [
						summaryDataResponse,
						personalDataResponse,
						loginDataResponse,
						customDataResponse,
						userLearningPlansDataResponse,
						userAssessmentsDataResponse
					] = await Promise.all([
						fetch(userSummaryDataUrl).then((response) => response.json()),
						fetch(userPersonalDataUrl).then((response) => response.json()),
						fetch(userLoginDataUrl).then((response) => response.json()),
						fetch(userCustomDataUrl).then((response) => response.json()),
						fetch(
							`${props.user_learning_plans_endpoint}?user_id=${cdxId}`
						).then((response) => response.json()),
						fetch(
							`${props.get_assessments_by_user_endpoint}?user_id=${cdxId}`
						).then((response) => {
							if (response.status === 200) {
								return response.json();
							} else if (response.status === 204) {
								// console.log(
								// 	'UserProfilePage - getAssessmentsByUser - no content'
								// );
								return [];
							}
						})
					]);

					const summaryData = summaryDataResponse.data || {};
					const personalData = personalDataResponse.data || {};
					const loginData = loginDataResponse.data || {};
					const customData = customDataResponse.data || {};
					const userLearningPlans = userLearningPlansDataResponse || [];
					const userAssessments = userAssessmentsDataResponse || [];

					setUserData({
						cdxIdData: cdxIdData.data,
						summaryData: summaryData,
						personalData: personalData,
						loginData: loginData,
						customData: customData,
						userLearningPlans: userLearningPlans,
						userAssessments: userAssessments
					});

					setLoading(false);
				} catch (error) {
					setError(error);
					setLoading(false);
				}
			};

			fetchData();
		},
		[
			id,
			userCdxIdDataUrl,
			userSummaryDataUrl,
			userPersonalDataUrl,
			userLoginDataUrl,
			userCustomDataUrl,
			props.user_learning_plan_exams_endpoint,
			props.get_assessments_by_user_endpoint
		]
	);

	useEffect(
		() => {
			// console.log('UserProfilePage - userData', userData);
		},
		[
			userData
		]
	);

	const updateRolesData = (updatedRolesData) => {
		setUserData((prevUserData) => ({
			...prevUserData,
			summaryData: {
				...prevUserData.summaryData,
				summary: {
					...prevUserData.summaryData.summary,
					roles: updatedRolesData
				}
			}
		}));
	};
	const updateLoginData = (updatedLoginData) => {
		setUserData((prevUserData) => ({
			...prevUserData,
			loginData: updatedLoginData
		}));
	};
	const updatePersonalData = (updatedPersonalData) => {
		setUserData((prevUserData) => ({
			...prevUserData,
			personalData: updatedPersonalData
		}));
	};
	const updateCustomData = (updatedCustomData) => {
		setUserData((prevUserData) => ({
			...prevUserData,
			customData: updatedCustomData
		}));
	};

	function determineDisplayName() {
		if (
			loading ||
			!userData ||
			!userData.summaryData ||
			!userData.personalData
		) {
			return null;
		}

		const { summaryData: { summary: { sortingName } = {} } = {} } = userData;

		const firstName = userData.personalData.fields.find(
			(obj) => obj.name === 'firstName' && obj.value && obj.value.asString
		);
		// console.log('UserProfilePage - firstName', firstName);
		const lastName = userData.personalData.fields.find(
			(obj) => obj.name === 'lastName' && obj.value && obj.value.asString
		);
		// console.log('UserProfilePage - lastName', lastName);
		const emailAddress = userData.personalData.fields.find(
			(obj) => obj.name === 'emailAddress' && obj.value && obj.value.asString
		);
		// console.log('UserProfilePage - emailAddress', emailAddress);

		if (firstName && lastName) {
			return `${firstName.value.asString} ${lastName.value.asString}`;
		} else if (firstName) {
			return firstName.value.asString;
		} else if (emailAddress) {
			return emailAddress.value.asString;
		} else if (sortingName) {
			return sortingName;
		} else {
			return I18n.t('admin.user_profile.display_name_not_available');
		}
	}

	if (error) {
		return <div>Error occurred: {error.message}</div>;
	}

	return (
		<div>
			<Breadcrumb
				category={I18n.t('admin.user_profile.users')}
				subcategory={determineDisplayName()}
				pageName={I18n.t('admin.user_profile.users').toUpperCase()}
			/>
			<UserProfileCard
				loading={loading}
				userId={id}
				userData={userData}
				determineDisplayName={determineDisplayName}
				courses={props.courses}
				iam_get_user_login_endpoint={props.iam_get_user_login_endpoint}
				iam_get_user_personal_endpoint={props.iam_get_user_personal_endpoint}
				iam_get_user_custom_endpoint={props.iam_get_user_custom_endpoint}
				iam_get_user_roles_endpoint={props.iam_get_user_roles_endpoint}
				iam_update_is_reportable_endpoint={
					props.iam_update_is_reportable_endpoint
				}
				iam_update_user_roles_endpoint={props.iam_update_user_roles_endpoint}
				iam_update_user_profile_endpoint={
					props.iam_update_user_profile_endpoint
				}
				user_learning_plan_exams_endpoint={
					props.user_learning_plan_exams_endpoint
				}
				lock_unlock_assessment_endpoint={props.lock_unlock_assessment_endpoint}
				score_released_assessment_endpoint={
					props.score_released_assessment_endpoint
				}
				updateRolesData={updateRolesData}
				updateLoginData={updateLoginData}
				updatePersonalData={updatePersonalData}
				updateCustomData={updateCustomData}
				save_personal_data_endpoint={props.save_personal_data_endpoint}
				permissionsHelper={props.permissionsHelper}
			/>
		</div>
	);
}
